import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import Avatar from 'react-avatar';
import { Col } from 'reactstrap';

import AppLoading from 'components/App/Loading';
import AppPoint from 'components/App/AppPoint';
import Button from 'components/App/Button';
import Breadcrumb from 'components/App/Breadcrumb';
import Card from 'components/App/Card';
import ProgressBar from 'components/ProgressBar';
import CustomerAccountTypeBadge from 'components/App/CustomerAccountTypeBadge';
import ModalRequestAction from 'components/Modals/ModalRequestAction';

import images from 'assets/images';
import { ReactComponent as BedSVG } from 'assets/images/bed.svg';
import { ReactComponent as UnitPriceSVG } from 'assets/images/unit-price.svg';
import { LOCATIONS, LOGIN_TYPE, ROOM_TYPE, storageKey } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import { responseReviewUserValidation } from 'utils/validate';
import formatMoney from 'utils/formatMoney';
import {
  convertIsoDateToString,
  getPlaceIdFromReview,
  unparseCurrency,
} from 'utils';
import renderOverviewText from 'utils/renderOverviewText';

import styles from './ReviewDetail.module.scss';

const REVIEW_CRITERIA_LEFT = [
  { key: 'roomReview', label: 'Phòng' },
  { key: 'cleanLinessReview', label: 'Sạch sẽ' },
];

const REVIEW_CRITERIA_RIGHT = [
  { key: 'facilityServiceReview', label: 'Tiện ích & Dịch vụ' },
  { key: 'staffReview', label: 'Nhân viên' },
];

function ReviewDetail(props) {
  const {
    isLoadingGetBookingReviewDetail,
    isLoadingPostFeedbackBookingReview,
    isLoadingUpdateBookingReview,
    isLoadingDeleteBookingReview,
    isLoadingUpdateFeedbackBookingReview,
    isLoadingDeleteFeedbackBookingReview,

    bookingReviewDetail,

    actionGetBookingReviewList,
    actionPostFeedbackBookingReview,
    actionUpdateBookingReview,
    actionDeleteBookingReview,
    actionUpdateFeedbackBookingReview,
    actionDeleteFeedbackBookingReview,
    breadcrumbList,
    isManagerReview,
  } = props;

  const [isOpenResponseReview, setIsOpenResponseReview] = React.useState(false);
  const [isEditingReview, setIsEditingReview] = React.useState(false);
  const [isOpenDeleteReview, setIsOpenDeleteReview] = React.useState(false);
  const [isEditingFeedback, setIsEditingFeedback] = React.useState(false);
  const [isOpenDeleteFeedback, setIsOpenDeleteFeedback] = React.useState(false);

  const history = useHistory();
  const location = useLocation();
  const filter = React.useMemo(
    () => ({
      placeId: new URLSearchParams(location.search).get('place'),
      reviewId: new URLSearchParams(location.search).get('review'),
    }),
    [location],
  );

  const isExtranet = localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET;

  const resolvedPlaceId = React.useMemo(() => (
    filter.placeId
    || getPlaceIdFromReview(bookingReviewDetail)
    || ''
  ), [bookingReviewDetail, filter.placeId]);

  const getRelativeDateText = React.useCallback((date) => {
    if (!date) return null;

    const timeDifference = new Date() - new Date(date);

    switch (true) {
      case Math.floor(timeDifference / (1000 * 60 * 60 * 24)) > 0:
        return `${Math.floor(timeDifference / (1000 * 60 * 60 * 24))} ngày trước`;

      case Math.floor(timeDifference / (1000 * 60 * 60)) > 0:
        return `${Math.floor(timeDifference / (1000 * 60 * 60))} giờ trước`;

      case Math.floor(timeDifference / (1000 * 60)) > 0:
        return `${Math.floor(timeDifference / (1000 * 60))} phút trước`;

      default:
        return null;
    }
  }, []);

  const renderDateReview = React.useMemo(
    () => getRelativeDateText(bookingReviewDetail.createdAt),
    [bookingReviewDetail.createdAt, getRelativeDateText],
  );

  const renderManagerFeedbackDate = React.useMemo(
    () => getRelativeDateText(bookingReviewDetail.managerFeedback?.createdAt),
    [bookingReviewDetail.managerFeedback?.createdAt, getRelativeDateText],
  );

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      managerFeedback: '',
    },

    validationSchema: Yup.object(responseReviewUserValidation),

    onSubmit: (values) => {
      actionPostFeedbackBookingReview({
        reviewId: filter.reviewId,
        managerFeedback: values.managerFeedback,
      });

      setIsOpenResponseReview(false);
    },
  });

  const onToggleResponseReview = React.useCallback(() => {
    setIsOpenResponseReview((prev) => !prev);
  }, []);

  const editReviewValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      content: bookingReviewDetail.content || '',
    },
    validationSchema: Yup.object({
      content: Yup.string().trim().required('Vui lòng nhập nội dung đánh giá'),
    }),
    onSubmit: (values) => {
      actionUpdateBookingReview({
        reviewId: filter.reviewId,
        content: values.content.trim(),
        callback: () => setIsEditingReview(false),
      });
    },
  });

  const onOpenEditReview = React.useCallback(() => {
    setIsEditingReview(true);
  }, []);

  const onCancelEditReview = React.useCallback(() => {
    editReviewValidation.resetForm();
    setIsEditingReview(false);
  }, [editReviewValidation]);

  const onConfirmDeleteReview = React.useCallback(() => {
    actionDeleteBookingReview({
      reviewId: filter.reviewId,
      callback: () => history.push(LOCATIONS.MANAGER_REVIEWS),
    });
  }, [actionDeleteBookingReview, filter.reviewId, history]);

  const editFeedbackValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      managerFeedback: bookingReviewDetail.managerFeedback?.feedback || '',
    },
    validationSchema: Yup.object(responseReviewUserValidation),
    onSubmit: (values) => {
      actionUpdateFeedbackBookingReview({
        reviewId: filter.reviewId,
        managerFeedback: values.managerFeedback.trim(),
        callback: () => setIsEditingFeedback(false),
      });
    },
  });

  const onOpenEditFeedback = React.useCallback(() => {
    setIsEditingFeedback(true);
  }, []);

  const onCancelEditFeedback = React.useCallback(() => {
    editFeedbackValidation.resetForm();
    setIsEditingFeedback(false);
  }, [editFeedbackValidation]);

  const onConfirmDeleteFeedback = React.useCallback(() => {
    actionDeleteFeedbackBookingReview({
      reviewId: filter.reviewId,
      callback: () => setIsOpenDeleteFeedback(false),
    });
  }, [actionDeleteFeedbackBookingReview, filter.reviewId]);

  const locationBreadcrumbList = React.useMemo(
    () => [
      {
        name: 'Trang chủ',
        isActive: false,
        link: LOCATIONS.HOME_PAGE,
      },
      {
        name: 'Danh sách đánh giá',
        isActive: false,
        link: {
          pathname: LOCATIONS.LOCATION_DETAIL.DETAIL_REVIEWS,
          search: `?place=${resolvedPlaceId}`,
        },
      },
      {
        name: 'Chi tiết đánh giá',
        isActive: true,
      },
    ],
    [resolvedPlaceId],
  );

  React.useEffect(() => {
    if (filter.reviewId) {
      actionGetBookingReviewList({ reviewId: filter.reviewId });
    }
  }, [actionGetBookingReviewList, filter.reviewId]);

  React.useEffect(() => {
    if (!filter.reviewId || filter.placeId || !resolvedPlaceId) return;

    history.replace({
      pathname: location.pathname,
      search: `?place=${resolvedPlaceId}&review=${filter.reviewId}`,
    });
  }, [
    filter.placeId,
    filter.reviewId,
    history,
    location.pathname,
    resolvedPlaceId,
  ]);

  const { userBooking } = bookingReviewDetail;

  const customerLevel = React.useMemo(() => (
    bookingReviewDetail.user?.level
    || userBooking?.userInfo?.level
    || ''
  ), [bookingReviewDetail.user?.level, userBooking?.userInfo?.level]);

  const bookingDetailRows = React.useMemo(() => ([
    {
      label: 'Mã đặt phòng',
      content: userBooking?.orderId || '-',
      isOrderId: true,
    },
    {
      label: 'Thời gian nhận phòng dự kiến',
      content: userBooking?.estimatedCheckInAt || '-',
    },
    {
      label: 'Ngày nhận phòng',
      content: convertIsoDateToString(userBooking?.startedAt) || '-',
    },
    {
      label: 'Ngày trả phòng',
      content: convertIsoDateToString(userBooking?.endedAt) || '-',
    },
    {
      label: 'Thông tin khách đặt chỗ',
      content: userBooking?.userInfo?.fullName
        || bookingReviewDetail.user?.fullName
        || '-',
      accountType: customerLevel,
    },
  ]), [bookingReviewDetail.user?.fullName, customerLevel, userBooking]);

  return (
    <div className="page-content">
      <Breadcrumb
        pageTitle="Chi tiết đánh giá"
        breadcrumbList={breadcrumbList || locationBreadcrumbList}
      />

      <div className={styles.Container}>
        {isLoadingGetBookingReviewDetail ? (
          <AppLoading loading={isLoadingGetBookingReviewDetail} />
        ) : (
          <>
            <Col lg="12" xl="4" className={styles.LeftColumn}>
              <Card className={styles.BookingInformation}>
                <div className={styles.Title}>
                  <div className={styles.TitleIcon}>
                    <BedSVG />
                  </div>

                  <p>Thông tin đơn đặt chỗ</p>
                </div>

                <div className={styles.Detail}>
                  {bookingDetailRows.map((item) => (
                    <div className={styles.Item} key={item.label}>
                      <p className={styles.ItemLabel}>
                        {item.isOrderId ? `${item.label}:` : item.label}
                      </p>

                      <p
                        className={`${styles.ItemContent}${
                          item.isOrderId ? ` ${styles.OrderIdValue}` : ''
                        }`}
                      >
                        <span>{item.content}</span>

                        {item.accountType && (
                          <CustomerAccountTypeBadge level={item.accountType} />
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className={styles.PriceUnit}>
                <div className={styles.Title}>
                  <div className={styles.TitleIcon}>
                    <UnitPriceSVG />
                  </div>

                  <p>Đơn giá</p>
                </div>

                {userBooking?.rooms?.map((item) => (
                  <div className={styles.PriceUnitItem} key={item._id}>
                    <div className={styles.Item}>
                      <p className={styles.ItemLabel}>
                        {item.parentRoomName
                          ? ROOM_TYPE[item.parentRoomName]?.label
                          : item.name}
                      </p>

                      <p className={styles.ItemContent}>
                        <span>x</span>
                        {item.roomQuantity}
                      </p>
                    </div>

                    <div className={styles.Item}>
                      <p className={styles.ItemLabel}>Giá phòng</p>
                      <p className={styles.ItemContent}>
                        {formatMoney.format(item.roomPrice)}
                      </p>
                    </div>
                  </div>
                ))}

                <div className={`${styles.PriceUnitItem} ${styles.PriceUnitItemTotal}`}>
                  <div className={styles.Item}>
                    <p className={styles.ItemLabel}>Tổng giá</p>

                    <div className={styles.ItemContentDiscountContainer}>
                      {!!userBooking?.totalDiscountedPrice && (
                        <p
                          className={`${styles.ItemContent} ${styles.ItemContentDiscount}`}
                        >
                          {`${unparseCurrency(userBooking?.totalPrice)} VND`}
                        </p>
                      )}

                      <p className={`${styles.ItemContent} ${styles.ItemContentTotal}`}>
                        {`${unparseCurrency(
                          userBooking?.totalDiscountedPrice || userBooking?.totalPrice,
                        )} VND`}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>

            <Col lg="12" xl="8" className={styles.RightColumn}>
              <Card className={styles.ReviewScore}>
                <div className={styles.ReviewScoreHeader}>
                  <AppPoint
                    point={bookingReviewDetail.overviewReview}
                    className={styles.OverviewPoint}
                  />

                  <p className={styles.OverviewLabel}>
                    {renderOverviewText(bookingReviewDetail.overviewReview)}
                  </p>
                </div>

                <div className={styles.ProgressbarWrap}>
                  <div className={styles.ProgressbarColumn}>
                    {REVIEW_CRITERIA_LEFT.map((item) => (
                      <ProgressBar
                        key={item.key}
                        title={item.label}
                        value={bookingReviewDetail[item.key] || 0}
                        className={styles.ProgressBar}
                      />
                    ))}
                  </div>

                  <div className={styles.ProgressbarColumn}>
                    {REVIEW_CRITERIA_RIGHT.map((item) => (
                      <ProgressBar
                        key={item.key}
                        title={item.label}
                        value={bookingReviewDetail[item.key] || 0}
                        className={styles.ProgressBar}
                      />
                    ))}
                  </div>
                </div>
              </Card>

              <Card className={styles.ResponseReview}>
                <div className={styles.Title}>
                  <p>PHẢN HỒI CỦA KHÁCH HÀNG</p>
                </div>

                <div className={styles.ResponseWrap}>
                  <div className={styles.Comment}>
                    <div className={styles.Avatar}>
                      <Avatar
                        src={bookingReviewDetail.user?.avatar}
                        className={styles.Avatar}
                        alt="user comment avatar alt"
                        name={bookingReviewDetail.user?.fullName}
                      />
                    </div>

                    <div className={styles.Item}>
                      <div className={styles.User}>
                        <p className={styles.Name}>
                          {bookingReviewDetail.user?.fullName}
                        </p>
                        <p className={styles.PostDate}>{renderDateReview}</p>
                      </div>

                      {isManagerReview && isEditingReview ? (
                        <form
                          className={styles.EditReviewForm}
                          onSubmit={editReviewValidation.handleSubmit}
                        >
                          <textarea
                            className={styles.EditReviewContent}
                            id="content"
                            name="content"
                            placeholder="Nhập nội dung đánh giá"
                            rows={4}
                            value={editReviewValidation.values.content}
                            onChange={editReviewValidation.handleChange}
                            onBlur={editReviewValidation.handleBlur}
                            maxLength={1000}
                          />

                          {!!editReviewValidation.errors.content
                            && editReviewValidation.touched.content && (
                            <p className={styles.Error}>
                              {editReviewValidation.errors.content}
                            </p>
                          )}

                          <div className={styles.EditReviewSubmit}>
                            <Button
                              isDefault
                              classes={styles.CancelEditReviewButton}
                              onClick={onCancelEditReview}
                              disabled={isLoadingUpdateBookingReview}
                            >
                              Hủy
                            </Button>

                            <Button
                              isDefault
                              type="submit"
                              classes={styles.SaveEditReviewButton}
                              disabled={isLoadingUpdateBookingReview}
                            >
                              {isLoadingUpdateBookingReview ? 'Đang xác nhận...' : 'Xác nhận'}
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <p className={styles.UserComment}>
                          {bookingReviewDetail.content}
                        </p>
                      )}

                      {isManagerReview ? !isEditingReview && (
                        <div className={styles.ReviewActions}>
                          <Button
                            isDefault
                            classes={styles.EditReviewButton}
                            onClick={onOpenEditReview}
                          >
                            Chỉnh sửa
                          </Button>

                          <Button
                            isDefault
                            classes={styles.DeleteReviewButton}
                            onClick={() => setIsOpenDeleteReview(true)}
                          >
                            Xóa
                          </Button>
                        </div>
                      ) : isExtranet && !bookingReviewDetail.managerFeedback && (
                        <Button
                          isDefault
                          classes={styles.ResponseUserButton}
                          onClick={onToggleResponseReview}
                        >
                          Phản hồi
                        </Button>
                      )}
                    </div>
                  </div>

                  {bookingReviewDetail.managerFeedback && (
                    <div className={styles.CommentAdmin}>
                      <div className={styles.Avatar}>
                        <img
                          src={bookingReviewDetail.managerFeedback
                            ?.createdBy?.avatar || images.avatar3.default}
                          className={styles.Avatar}
                          alt="user comment avatar alt"
                        />
                      </div>

                      <div className={styles.Item}>
                        <div className={styles.User}>
                          <p className={styles.Name}>Bạn</p>
                          <p className={styles.PostDate}>{renderManagerFeedbackDate}</p>
                        </div>

                        {isManagerReview && isEditingFeedback ? (
                          <form
                            className={styles.EditReviewForm}
                            onSubmit={editFeedbackValidation.handleSubmit}
                          >
                            <textarea
                              className={styles.EditReviewContent}
                              id="managerFeedback"
                              name="managerFeedback"
                              placeholder="Nhập phản hồi"
                              rows={4}
                              value={editFeedbackValidation.values.managerFeedback}
                              onChange={editFeedbackValidation.handleChange}
                              onBlur={editFeedbackValidation.handleBlur}
                              maxLength={300}
                            />

                            {!!editFeedbackValidation.errors.managerFeedback
                              && editFeedbackValidation.touched.managerFeedback && (
                              <p className={styles.Error}>
                                {editFeedbackValidation.errors.managerFeedback}
                              </p>
                            )}

                            <div className={styles.EditReviewSubmit}>
                              <Button
                                isDefault
                                classes={styles.CancelEditReviewButton}
                                onClick={onCancelEditFeedback}
                                disabled={isLoadingUpdateFeedbackBookingReview}
                              >
                                Hủy
                              </Button>

                              <Button
                                isDefault
                                type="submit"
                                classes={styles.SaveEditReviewButton}
                                disabled={isLoadingUpdateFeedbackBookingReview}
                              >
                                {isLoadingUpdateFeedbackBookingReview
                                  ? 'Đang xác nhận...'
                                  : 'Xác nhận'}
                              </Button>
                            </div>
                          </form>
                        ) : (
                          <p className={styles.UserComment}>
                            {bookingReviewDetail.managerFeedback.feedback}
                          </p>
                        )}

                        {isManagerReview && !isEditingFeedback && (
                          <div className={styles.ReviewActions}>
                            <Button
                              isDefault
                              classes={styles.EditReviewButton}
                              onClick={onOpenEditFeedback}
                            >
                              Chỉnh sửa
                            </Button>

                            <Button
                              isDefault
                              classes={styles.DeleteReviewButton}
                              onClick={() => setIsOpenDeleteFeedback(true)}
                            >
                              Xóa
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {isOpenResponseReview && (
                    <form
                      className={styles.ResponseForm}
                      onSubmit={validation.handleSubmit}
                    >
                      <textarea
                        className={styles.ResponseInput}
                        placeholder="Nhập mô tả"
                        rows={4}
                        id="managerFeedback"
                        name="managerFeedback"
                        value={validation.values.managerFeedback}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        maxLength={300}
                      />

                      {!!get(validation.errors, 'managerFeedback') && (
                        <p className={styles.Error}>
                          {get(validation.errors, 'managerFeedback')}
                        </p>
                      )}

                      <div className={styles.ResponseSubmit}>
                        <button
                          type="submit"
                          disabled={isLoadingPostFeedbackBookingReview}
                        >
                          Gửi
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </Card>
            </Col>
          </>
        )}
      </div>

      {isManagerReview && (
        <>
          <ModalRequestAction
            isOpen={isOpenDeleteReview}
            icon={images.modalGarbageCan.default}
            textRequest={'Bạn có chắc chắn muốn xóa \n đánh giá này không?'}
            onCloseModal={() => setIsOpenDeleteReview(false)}
            onConfirm={onConfirmDeleteReview}
            isLoadingConfirm={isLoadingDeleteBookingReview}
            footerClassName={styles.DeleteReviewModalFooter}
          />

          <ModalRequestAction
            isOpen={isOpenDeleteFeedback}
            icon={images.modalGarbageCan.default}
            textRequest={'Bạn có chắc chắn muốn xóa \n phản hồi này không?'}
            onCloseModal={() => setIsOpenDeleteFeedback(false)}
            onConfirm={onConfirmDeleteFeedback}
            isLoadingConfirm={isLoadingDeleteFeedbackBookingReview}
            footerClassName={styles.DeleteReviewModalFooter}
          />
        </>
      )}
    </div>
  );
}

ReviewDetail.propTypes = {
  isLoadingGetBookingReviewDetail: PropTypes.bool.isRequired,
  isLoadingPostFeedbackBookingReview: PropTypes.bool.isRequired,
  isLoadingUpdateBookingReview: PropTypes.bool.isRequired,
  isLoadingDeleteBookingReview: PropTypes.bool.isRequired,
  isLoadingUpdateFeedbackBookingReview: PropTypes.bool.isRequired,
  isLoadingDeleteFeedbackBookingReview: PropTypes.bool.isRequired,

  bookingReviewDetail: PropTypes.instanceOf(Object),

  actionGetBookingReviewList: PropTypes.func.isRequired,
  actionPostFeedbackBookingReview: PropTypes.func.isRequired,
  actionUpdateBookingReview: PropTypes.func.isRequired,
  actionDeleteBookingReview: PropTypes.func.isRequired,
  actionUpdateFeedbackBookingReview: PropTypes.func.isRequired,
  actionDeleteFeedbackBookingReview: PropTypes.func.isRequired,
  breadcrumbList: PropTypes.instanceOf(Array),
  isManagerReview: PropTypes.bool,
};

ReviewDetail.defaultProps = {
  bookingReviewDetail: INITIAL_VALUE.OBJECT,
  breadcrumbList: null,
  isManagerReview: false,
};

export default ReviewDetail;
